import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Loader2, CheckCircle, ChevronDown, ChevronUp, Lock,
  Target, BookOpen, FileText, Play, Pause, Mic, Shield,
  TrendingUp, AlertTriangle, Zap, Star, ArrowLeft
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const GOLD = '#C9A961';

const LessonPage = () => {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [module, setModule] = useState(null);
  const [course, setCourse] = useState(null);
  const [ncaa, setNcaa] = useState(null);
  const [openSections, setOpenSections] = useState({ playbook: true });
  const [reasoningText, setReasoningText] = useState('');
  const [reasoningSubmitted, setReasoningSubmitted] = useState(false);
  const [reasoningFeedback, setReasoningFeedback] = useState(null);
  const [reasoningLoading, setReasoningLoading] = useState(false);
  const [quizUnlocked, setQuizUnlocked] = useState(false);
  const [quizActive, setQuizActive] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [playingVoice, setPlayingVoice] = useState(false);
  const [aiFeedbacks, setAiFeedbacks] = useState({});
  const [feedbackLoading, setFeedbackLoading] = useState({});
  const synthRef = useRef(null);

  const token = localStorage.getItem('academy_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const [modRes, ncaaRes] = await Promise.all([
        api.get(`/school/course/${courseId}/modules`, { headers }),
        api.get('/school/ncaa-eligibility', { headers })
      ]);
      const found = (modRes.data.modules || []).find(m => m.id === moduleId);
      setModule(found);
      setCourse(modRes.data.course);
      setNcaa(ncaaRes.data.eligibility);
      if (found?.completed) { setQuizUnlocked(true); setReasoningSubmitted(true); }
    } catch (err) {
      if (err.response?.status === 401) navigate('/academy/login');
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  }, [courseId, moduleId, token, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const submitReasoning = async () => {
    const words = reasoningText.trim().split(/\s+/).length;
    if (words < 8) {
      toast.error('Go deeper. Explain the mechanics — at least 2-3 sentences.');
      return;
    }
    setReasoningLoading(true);
    try {
      const res = await api.post('/school/ai-feedback', {
        module_id: moduleId,
        question_id: 'reasoning_gate',
        response_text: reasoningText
      }, { headers });
      setReasoningFeedback(res.data.feedback);
      const notes = res.data.feedback?.coaching_notes || '';
      if (notes.includes('INCOMPLETE') || words < 15) {
        toast.error('Your reasoning needs more depth. Expand and resubmit.');
      } else {
        setReasoningSubmitted(true);
        setQuizUnlocked(true);
        toast.success('Reasoning accepted. Assessment unlocked.');
      }
    } catch { toast.error('Feedback failed'); }
    finally { setReasoningLoading(false); }
  };

  const playVoiceCoach = () => {
    if (!module?.content?.deep_dive?.length) return;
    if (playingVoice) {
      window.speechSynthesis?.cancel();
      setPlayingVoice(false);
      return;
    }
    const text = module.content.deep_dive.join(' ');
    if ('speechSynthesis' in window) {
      const utt = new SpeechSynthesisUtterance(text);
      utt.rate = 0.92; utt.pitch = 0.85;
      utt.onend = () => setPlayingVoice(false);
      synthRef.current = utt;
      setPlayingVoice(true);
      window.speechSynthesis.speak(utt);
    }
  };

  const submitQuiz = async () => {
    if (!module) return;
    setSubmittingQuiz(true);
    try {
      const res = await api.post('/school/quiz/submit',
        { module_id: moduleId, answers: quizAnswers }, { headers }
      );
      setQuizResult(res.data);
      toast.success(`Score: ${res.data.score}% (${res.data.letter_grade})`);
      // Get AI feedback for open responses
      const openQs = module.quiz?.questions?.filter(q => q.type === 'open_response') || [];
      for (const q of openQs) {
        if (quizAnswers[q.id] && typeof quizAnswers[q.id] === 'string' && quizAnswers[q.id].length > 10) {
          getAiFeedback(q.id, quizAnswers[q.id]);
        }
      }
    } catch { toast.error('Submission failed'); }
    finally { setSubmittingQuiz(false); }
  };

  const getAiFeedback = async (questionId, responseText) => {
    setFeedbackLoading(prev => ({ ...prev, [questionId]: true }));
    try {
      const res = await api.post('/school/ai-feedback', {
        module_id: moduleId, question_id: questionId, response_text: responseText
      }, { headers });
      setAiFeedbacks(prev => ({ ...prev, [questionId]: res.data.feedback }));
    } catch {}
    finally { setFeedbackLoading(prev => ({ ...prev, [questionId]: false })); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#08080a] flex items-center justify-center">
      <div className="w-12 h-12 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!module) return (
    <div className="min-h-screen bg-[#08080a] pt-24 flex items-center justify-center">
      <p className="text-gray-500">Module not found</p>
    </div>
  );

  const c = module.content || {};
  const questions = module.quiz?.questions || [];
  const mcQuestions = questions.filter(q => q.type !== 'open_response');
  const orQuestions = questions.filter(q => q.type === 'open_response');

  return (
    <div className="min-h-screen bg-[#08080a] pt-20" data-testid="lesson-page">
      {/* Top Bar */}
      <div className="border-b border-gray-800/50 bg-[#0a0a0c]">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => navigate('/academy/school')} className="text-gray-500 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-black text-sm lg:text-base">{module.title}</h1>
            <div className="text-xs text-gray-500">{course?.name} | Standard: {module.standard_code}</div>
          </div>
          {module.completed && <span className="text-green-400 text-xs font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> COMPLETE</span>}
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="max-w-[1400px] mx-auto flex gap-0 lg:gap-1 min-h-[calc(100vh-120px)]">

        {/* LEFT: Strategy Room (20%) */}
        <aside className="hidden lg:block w-[20%] p-4 border-r border-gray-800/30 sticky top-20 h-[calc(100vh-80px)] overflow-y-auto">
          {/* Scout Report */}
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: GOLD }}>Scout Report</div>
            <div className="bg-[#0e0e12] rounded-lg p-3 space-y-3 border border-gray-800/30">
              <div>
                <div className="text-[9px] text-gray-600 uppercase">Standard</div>
                <div className="text-white font-bold text-sm font-mono">{module.standard_code}</div>
                <div className="text-gray-400 text-[10px] mt-0.5">{module.standard_domain}</div>
              </div>
              {ncaa && (
                <div>
                  <div className="text-[9px] text-gray-600 uppercase">CIF Status</div>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                    ncaa.risk_level === 'elite' ? 'bg-green-500/20 text-green-400' :
                    ncaa.risk_level === 'on_track' ? 'bg-blue-500/20 text-blue-400' :
                    ncaa.risk_level === 'at_risk' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>{ncaa.risk_level === 'lockout' ? 'HOLD' : ncaa.risk_level?.toUpperCase()}</span>
                  <div className="text-[10px] text-gray-500 mt-1">Core GPA: {ncaa.core_gpa.toFixed(2)}</div>
                </div>
              )}
              {/* Skills Radar */}
              <div>
                <div className="text-[9px] text-gray-600 uppercase mb-2">Module Progress</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-800 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: module.completed ? '100%' : reasoningSubmitted ? '75%' : '25%', backgroundColor: GOLD }} />
                  </div>
                  <span className="text-[10px] text-gray-500">{module.completed ? '100' : reasoningSubmitted ? '75' : '25'}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Vocabulary Drill (sticky reference) */}
          {c.vocabulary?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: GOLD }}>Vocabulary Drill</div>
              <div className="space-y-2">
                {c.vocabulary.map((v, i) => (
                  <div key={i} className="bg-[#0e0e12] rounded p-2 border border-gray-800/30">
                    <div className="text-white font-bold text-[11px]">{v.term}</div>
                    <div className="text-gray-500 text-[10px] leading-snug mt-0.5">{v.definition}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* CENTER: Main Court (60%) */}
        <main className="flex-1 lg:w-[60%] p-4 lg:p-8 overflow-y-auto">

          {/* Section: The Playbook */}
          {c.deep_dive?.length > 0 && (
            <section className="mb-6">
              <button onClick={() => toggleSection('playbook')} className="w-full flex items-center justify-between py-3 border-b" style={{ borderColor: `${GOLD}40` }}>
                <span className="text-sm font-black uppercase tracking-wider" style={{ color: GOLD }}>The Game Plan</span>
                {openSections.playbook ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
              </button>
              {openSections.playbook && (
                <div className="pt-4 space-y-4">
                  {c.deep_dive.map((para, pi) => (
                    <p key={pi} className="text-gray-300 text-sm leading-[1.8] tracking-wide">{para}</p>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Section: Deep Dive */}
          {c.sports_logic && (
            <section className="mb-6">
              <button onClick={() => toggleSection('deepdive')} className="w-full flex items-center justify-between py-3 border-b" style={{ borderColor: `${GOLD}40` }}>
                <span className="text-sm font-black uppercase tracking-wider" style={{ color: GOLD }}>Sports Logic: {c.sports_logic.title}</span>
                {openSections.deepdive ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
              </button>
              {openSections.deepdive && (
                <div className="pt-4 p-4 rounded-lg border" style={{ borderColor: `${GOLD}30`, backgroundColor: 'rgba(201,169,97,0.03)' }}>
                  <p className="text-gray-300 text-sm leading-[1.8]">{c.sports_logic.case_study}</p>
                </div>
              )}
            </section>
          )}

          {/* Mobile Vocabulary (hidden on desktop) */}
          {c.vocabulary?.length > 0 && (
            <section className="mb-6 lg:hidden">
              <button onClick={() => toggleSection('vocab')} className="w-full flex items-center justify-between py-3 border-b border-gray-800">
                <span className="text-sm font-black uppercase tracking-wider text-gray-400">Vocabulary Drill</span>
                {openSections.vocab ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
              </button>
              {openSections.vocab && (
                <div className="pt-3 grid grid-cols-1 gap-2">
                  {c.vocabulary.map((v, i) => (
                    <div key={i} className="bg-[#0e0e12] rounded p-3">
                      <span className="text-white font-bold text-xs">{v.term}: </span>
                      <span className="text-gray-400 text-xs">{v.definition}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* REASONING GATE */}
          <section className="mb-8 p-5 rounded-xl border-2" style={{ borderColor: reasoningSubmitted ? '#22c55e40' : `${GOLD}60`, backgroundColor: reasoningSubmitted ? 'rgba(34,197,94,0.03)' : 'rgba(201,169,97,0.05)' }}>
            <div className="flex items-center gap-2 mb-3">
              {reasoningSubmitted ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Zap className="w-5 h-5" style={{ color: GOLD }} />}
              <span className="text-sm font-black uppercase tracking-wider" style={{ color: reasoningSubmitted ? '#22c55e' : GOLD }}>
                {reasoningSubmitted ? 'Reasoning Accepted' : 'Reasoning Gate'}
              </span>
              {!reasoningSubmitted && <Lock className="w-3 h-3 text-gray-500 ml-auto" />}
            </div>
            <p className="text-gray-400 text-xs mb-3">
              Explain in 2-3 sentences <strong className="text-white">HOW</strong> this concept applies to your sport or athletic training. Be specific about the mechanics.
            </p>
            <textarea
              value={reasoningText}
              onChange={(e) => setReasoningText(e.target.value)}
              disabled={reasoningSubmitted}
              placeholder="Example: The concept of rational vs. irrational numbers connects to basketball because..."
              className="w-full bg-[#0a0a0c] border border-gray-800 rounded-lg p-3 text-sm text-gray-300 min-h-[80px] focus:outline-none resize-y disabled:opacity-50"
              style={{ borderColor: reasoningSubmitted ? '#22c55e30' : undefined }}
              data-testid="reasoning-gate-input"
            />
            {reasoningFeedback && (
              <div className={`mt-3 p-3 rounded-lg text-xs ${reasoningSubmitted ? 'bg-green-500/10 border border-green-500/20 text-green-300' : 'bg-red-500/10 border border-red-500/20 text-red-300'}`}>
                <Mic className="w-3 h-3 inline mr-1" /> {reasoningFeedback.coaching_notes}
              </div>
            )}
            {!reasoningSubmitted && (
              <Button onClick={submitReasoning} disabled={reasoningLoading || reasoningText.trim().length < 20} className="mt-3 text-black font-bold text-xs" style={{ backgroundColor: GOLD }} data-testid="submit-reasoning-btn">
                {reasoningLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Reasoning'}
              </Button>
            )}
          </section>

          {/* ASSESSMENT */}
          {quizUnlocked && !quizResult && (
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4 py-3 border-b" style={{ borderColor: `${GOLD}40` }}>
                <Target className="w-5 h-5" style={{ color: GOLD }} />
                <span className="text-sm font-black uppercase tracking-wider" style={{ color: GOLD }}>Mastery Assessment</span>
              </div>

              {/* Foundation (MC) */}
              {mcQuestions.length > 0 && (
                <div className="mb-6">
                  <div className="text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-3">Level 1 — Foundation</div>
                  <div className="space-y-5">
                    {mcQuestions.map((q, qi) => (
                      <div key={q.id}>
                        <div className="text-sm text-white font-medium mb-2">
                          <span className="text-orange-500 font-bold mr-2">{qi + 1}.</span> {q.question}
                        </div>
                        <div className="space-y-1.5">
                          {q.options?.map((opt, oi) => (
                            <button key={oi} onClick={() => setQuizAnswers(prev => ({ ...prev, [q.id]: oi }))}
                              className={`w-full text-left p-3 rounded-lg border text-xs transition-all ${quizAnswers[q.id] === oi ? 'border-orange-500 bg-orange-500/10 text-white' : 'border-gray-800 bg-[#0e0e12] text-gray-400 hover:border-gray-600'}`}>
                              <span className="font-bold mr-2 text-gray-600">{String.fromCharCode(65 + oi)}.</span>{opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Advanced (Open Response) */}
              {orQuestions.length > 0 && (
                <div className="mb-6">
                  <div className="text-[10px] text-red-400 font-bold uppercase tracking-wider mb-3">Level 2-3 — Application & Analysis</div>
                  <div className="space-y-5">
                    {orQuestions.map((q, qi) => (
                      <div key={q.id} className="p-4 rounded-lg border border-gray-800/50 bg-[#0c0c0e]">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">{q.difficulty}</span>
                          <span className="text-[9px] text-gray-600">{q.points} pts</span>
                        </div>
                        <div className="text-sm text-white font-medium mb-3">{q.question}</div>
                        <textarea
                          value={quizAnswers[q.id] || ''}
                          onChange={(e) => setQuizAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                          placeholder="Show your work. Explain the Why and How."
                          className="w-full bg-[#08080a] border border-gray-800 rounded-lg p-3 text-xs text-gray-300 min-h-[90px] focus:outline-none focus:border-orange-500/50 resize-y"
                        />
                        {aiFeedbacks[q.id] && (
                          <div className="mt-2 p-3 rounded bg-orange-500/5 border border-orange-500/20">
                            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: GOLD }}>Coach's Notes</div>
                            <div className="text-xs text-gray-300">{aiFeedbacks[q.id].coaching_notes}</div>
                          </div>
                        )}
                        {feedbackLoading[q.id] && <Loader2 className="w-3 h-3 animate-spin text-orange-500 mt-2" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button onClick={submitQuiz} disabled={submittingQuiz || Object.keys(quizAnswers).length < mcQuestions.length}
                className="w-full py-6 text-black font-black text-sm" style={{ backgroundColor: GOLD }}>
                {submittingQuiz ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Assessment'}
              </Button>
            </section>
          )}

          {/* Quiz locked message */}
          {!quizUnlocked && !module.completed && (
            <div className="p-5 rounded-xl border border-gray-800/50 bg-[#0c0c0e] text-center">
              <Lock className="w-6 h-6 text-gray-600 mx-auto mb-2" />
              <div className="text-gray-500 text-sm">Complete the Reasoning Gate to unlock the assessment.</div>
            </div>
          )}

          {/* Quiz Results */}
          {quizResult && (
            <section className="mb-8 p-6 rounded-xl border" style={{ borderColor: quizResult.passed ? '#22c55e40' : '#ef444440' }}>
              <div className="text-center mb-4">
                <div className={`text-5xl font-black ${quizResult.passed ? 'text-green-400' : 'text-red-400'}`}>{quizResult.score}%</div>
                <div className={`text-sm font-bold ${quizResult.passed ? 'text-green-500' : 'text-red-500'}`}>
                  {quizResult.letter_grade} — {quizResult.passed ? 'MASTERY ACHIEVED' : 'NEEDS REVIEW'}
                </div>
              </div>
              {quizResult.results?.map((r, i) => (
                <div key={i} className={`p-2 rounded mb-1 flex items-center gap-2 ${r.correct ? 'bg-green-600/10' : 'bg-red-600/10'}`}>
                  {r.correct ? <CheckCircle className="w-3 h-3 text-green-500" /> : <AlertTriangle className="w-3 h-3 text-red-500" />}
                  <span className="text-xs text-gray-400">Q{i+1}: {r.correct ? 'Correct' : 'Review needed'} ({r.points_earned}pts)</span>
                </div>
              ))}
            </section>
          )}
        </main>

        {/* RIGHT: Training Kit (20%) */}
        <aside className="hidden lg:block w-[20%] p-4 border-l border-gray-800/30 sticky top-20 h-[calc(100vh-80px)] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-4" style={{ color: GOLD }}>Training Kit</div>

          {/* Voice Coach */}
          <Card className="bg-[#0e0e12] border-gray-800/30 mb-4">
            <CardContent className="p-3">
              <div className="text-[9px] text-gray-600 uppercase mb-2">Voice Coach</div>
              <Button onClick={playVoiceCoach} size="sm" className={`w-full text-xs font-bold ${playingVoice ? 'bg-red-600 hover:bg-red-700' : ''}`}
                style={!playingVoice ? { backgroundColor: GOLD, color: '#000' } : {}} data-testid="voice-coach-btn">
                {playingVoice ? <><Pause className="w-3 h-3 mr-1" /> Stop Coach</> : <><Play className="w-3 h-3 mr-1" /> Listen to Coach</>}
              </Button>
              <div className="text-[9px] text-gray-600 mt-2">ElevenLabs voice • Follow along</div>
            </CardContent>
          </Card>

          {/* Download Field Work */}
          {module.worksheet && (
            <Card className="bg-[#0e0e12] border-gray-800/30 mb-4">
              <CardContent className="p-3">
                <div className="text-[9px] text-gray-600 uppercase mb-2">Field Work</div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/school/module/${moduleId}/worksheet-pdf`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-white text-black font-bold text-xs py-2.5 rounded-lg hover:bg-gray-200 transition-colors"
                  data-testid="download-worksheet-btn">
                  <FileText className="w-3 h-3" /> Download PDF
                </a>
                <div className="text-[9px] text-gray-600 mt-2">Printable worksheet • Problem set + writing prompt + physical drill</div>
              </CardContent>
            </Card>
          )}

          {/* Progress Tracker */}
          <Card className="bg-[#0e0e12] border-gray-800/30 mb-4">
            <CardContent className="p-3">
              <div className="text-[9px] text-gray-600 uppercase mb-3">Lesson Progress</div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span className="text-[10px] text-gray-400">Read Game Plan</span>
                </div>
                <div className="flex items-center gap-2">
                  {openSections.deepdive ? <CheckCircle className="w-3 h-3 text-green-500" /> : <div className="w-3 h-3 rounded-full border border-gray-700" />}
                  <span className="text-[10px] text-gray-400">Sports Logic</span>
                </div>
                <div className="flex items-center gap-2">
                  {reasoningSubmitted ? <CheckCircle className="w-3 h-3 text-green-500" /> : <div className="w-3 h-3 rounded-full border border-gray-700" />}
                  <span className="text-[10px] text-gray-400">Reasoning Gate</span>
                </div>
                <div className="flex items-center gap-2">
                  {quizResult ? <CheckCircle className="w-3 h-3 text-green-500" /> : <div className="w-3 h-3 rounded-full border border-gray-700" />}
                  <span className="text-[10px] text-gray-400">Assessment</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Module Info */}
          <div className="text-[9px] text-gray-700 space-y-1">
            <div>Module ID: {moduleId?.slice(0,8)}</div>
            <div>Standard: {module.standard_code}</div>
            {module.quiz_score !== null && module.quiz_score !== undefined && <div>Last Score: {module.quiz_score}%</div>}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default LessonPage;
